import React, { useState, useEffect, useMemo } from 'react';
import { X, CheckCircle, CalendarArrowDown } from 'lucide-react';
import {
  ActionType, MoldOrder,
  MoldOrderStatus, User
} from '../../types/mold';
import FormField from '../shared/FormField';
import { useFormProgressFromMoldOrder } from '../../hooks/useFormProgress';
import {
  getDisplayReviewInfo,
  isFinalStep,
} from '../MoldDetail/utils';
import { useNavigate } from 'react-router-dom';
import UserProfileImage from '../KanbanBoard/UserProfileImage';
import { format } from 'date-fns';
import ViewQuestionnaireButton from '../shared/ViewQuestionnaireButton';
import CreateNewMoldOrderButton from '../shared/CreateNewMoldOrderButton';
import { FileResponse, PatchMoldOrderRequest } from '../../types/moldOrder';
import { getFormConfig } from '../Questionnaire/utils';
import { Section } from '../../types/form';
import useMoldOrderFiles from '../../hooks/useMoldOrderFiles';
import { validateIfUserHasPermission } from '../../utils/validationUtils';
import { useAuth } from '../../hooks/useAuth';
import { selectionConfigService, fileService } from '../../services/api';
import ImagesUploader from '../Questionnaire/ImagesUploader';
import ExpandableText from '../shared/ExpandableText';
import { getDraftRejectPhotoTag, getFinalRejectPhotoTag, getRejectPhotoTag } from '../utils/moldOrder';
import DeletingModal from '../shared/DeletingModal';
import AssignToMeButton from './AssignToMeButton';
import { LoadedFormConfig } from '../Questionnaire/formConfigLoader';
import FillQuestionnaireButton from './FillQuestionnaireButton';
import ActionButton from './ActionButton';
import { useAppState } from '../../context/AppStateContext';
import CustomModal, { CustomModalType } from './CustomModal';
import SmallConfirmModal from './SmallConfirmModal';
import NextAssigneeInfo from './NextAssigneeInfo';
import TagFileWarningModal from './TagFileWarningModal';
import GenericErrorModal from './GenericErrorModal';
import PrintQRCodeButton from './PrintQRCodeButton';
import NextStepSkeleton from './NextStepSkeleton';
import ApprovalButtons from './ApprovalButtons';
import { getBangkokDateWithOffset } from '../../utils/dateUtils';

interface NextStepActionBoxProps {
  moldOrder: MoldOrder;
  onStatusUpdate?: (action: ActionType, assigneeId: number | null | undefined) => Promise<void>;
  updateMoldOrderStateInstance: (moldOrder: MoldOrder, data: { notes?: string }) => Promise<void>;
  updateMoldOrder: (data: PatchMoldOrderRequest) => Promise<void>;
  updateMoldOrderDetail: (data: PatchMoldOrderRequest) => Promise<void>;
  downloadPdfReport: (id: number) => Promise<Blob | null>;
}

const NextStepActionBox: React.FC<NextStepActionBoxProps> = ({
  moldOrder,
  onStatusUpdate,
  updateMoldOrderStateInstance,
  updateMoldOrder,
  updateMoldOrderDetail,
  downloadPdfReport,
}) => {
  // =============================
  // Hooks & Context
  // =============================
  const navigate = useNavigate();
  const { user } = useAuth();

  // =============================
  // UI State
  // =============================
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSmallConfirmModalOpen, setIsSmallConfirmModalOpen] = useState(false);
  const [isDeletingAllDraftFiles, setIsDeletingAllDraftFiles] = useState(false);
  const [loadingPdfReportText, setLoadingPdfReportText] = useState("");

  // Modal content and confirmations
  const [modalContent, setModalContent] = useState<{
    title: string;
    message: string;
    type: CustomModalType;
    action?: ActionType;
  }>({
    title: '',
    message: '',
    type: 'confirmation',
  });

  // Error / feedback modals
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'tag-file-warning' | 'generic-error' | null>(null);

  // =============================
  // Data State
  // =============================
  const {
    featureToggles,
    isMoldOrderCheckedForFormCompletion,
    isAllowSetNextAssignee,
    getDefaultNextAssignee,
    getMoldOrderMainType,
    getActionButtonMapping,
    getNextStepActionConfig,
  } = useAppState();
  const [assignee, setAssignee] = useState<User>(moldOrder.assignedTo);
  const [nextAssignee, setNextAssignee] = useState<User | null | undefined>(getDefaultNextAssignee(moldOrder));
  const [note, setNote] = useState('');
  const [formConfig, setFormConfig] = useState<Section[]>([]);
  const [loadedFormConfig, setLoadedFormConfig] = useState<LoadedFormConfig>();
  const [formConfigLoading, setFormConfigLoading] = useState<boolean>(true);
  const [reporterRejectReasons, setReporterRejectReasons] = useState<string[]>([]);
  const updateActionFormValuesRef = React.useRef<Record<string, any>>({});

  // Get files and required utilities from the hook
  const { files, loading: filesLoading, hasRejectFile, fetchFiles } = useMoldOrderFiles(moldOrder.id);
  // Consolidated form progress (include loaded form config and current files to compute validation messages)
  const progress = useFormProgressFromMoldOrder({
    moldOrder,
    sections: formConfig,
    loadedFormConfig,
    currentFiles: files
  });

  // Derived values
  const approvalInfo = moldOrder.currentStateApproval;
  const hasPermission = validateIfUserHasPermission(user, moldOrder);
  const allowFormSubmission = useMemo(() => {
    const requireCheckForCompletion = isMoldOrderCheckedForFormCompletion(moldOrder.type);
    return !requireCheckForCompletion || (requireCheckForCompletion && progress.isComplete);
  }, [formConfig.length, formConfigLoading]);

  // =============================
  // Effects
  // =============================
  useEffect(() => {
    if (moldOrder) {
      fetchFiles();
    }
  }, [moldOrder?.id]);

  useEffect(() => {
    // reload form configuration whenever the moldOrder changes (id)
    if (!moldOrder?.id) return;

    const loadFormConfig = async () => {
      setFormConfigLoading(true);
      try {
        const moldOrderGroup = getMoldOrderMainType(moldOrder.type);
        const config = await getFormConfig(moldOrder, moldOrderGroup);
        if (config && config.sections) {
          setLoadedFormConfig(config);
          setFormConfig(config.sections);
        }
      } catch (error) {
        console.error('Failed to load form configuration', error);
      } finally {
        setFormConfigLoading(false);
      }
    };

    loadFormConfig();
  }, [moldOrder?.id, moldOrder?.status]);

  useEffect(() => {
    const fetchRejectReasons = async () => {
      try {
        const response = await selectionConfigService.getSelectionConfig(selectionConfigService.nameEnums.ReporterConfirmRejectReasons);
        const reasons = response?.data?.values?.map((item) => item.value) || [];
        setReporterRejectReasons(reasons);
      } catch (error) {
        console.error('Error fetching reporter reject reasons:', error);
        setReporterRejectReasons([]); // Set empty array as fallback
      }
    };

    fetchRejectReasons();
  }, []);

  // =============================
  // Helper functions (pure / derived)
  // =============================
  const getRejectFiles = (): FileResponse[] => files.filter(file => file.tag === getDraftRejectPhotoTag(moldOrder));

  // Determine if next assignee should be shown in modal
  const showNextAssineeSelection = ['accept', 'confirmation'].includes(modalContent.type) && isAllowSetNextAssignee(moldOrder.status, moldOrder.type);

  // Helper to get nextStepAction values (from user input or defaults)
  const getNextStepActionValues = () => {
    const formConfigs = getNextStepActionConfig(moldOrder.type, moldOrder.status);
    const values: Record<string, any> = {};
    
    formConfigs.forEach(config => {
      const fieldKey = String(config.key);
      
      // First priority: Use current form value if user has changed it
      if (updateActionFormValuesRef.current.hasOwnProperty(fieldKey)) {
        values[fieldKey] = updateActionFormValuesRef.current[fieldKey];
        return;
      }
      
      // Second priority: Use existing value from orderDetail
      if (moldOrder.orderDetail.hasOwnProperty(config.key) && moldOrder.orderDetail[config.key as keyof typeof moldOrder.orderDetail]) {
        return; // Already saved, no need to update
      }
      
      // Third priority: Use default value for date fields
      if (config.type === 'date' && config.defaultDateOffset !== undefined) {
        values[fieldKey] = getBangkokDateWithOffset(config.defaultDateOffset)
          .toISOString()
          .slice(0, 10);
      }
    });
    
    return values;
  };

  // =============================
  // Action handlers (mutations / API)
  // =============================
  const changeCurrentAssignee = async (newAssignee: User) => {
    try {
      await updateMoldOrder({ assignedTo: newAssignee.id });
      setAssignee(newAssignee);
    } catch (error) {
      console.error('Error updating mold order:', error);
      setModalContent({
        title: 'เกิดข้อผิดพลาด',
        message: 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง',
        type: 'error',
      });
    }
  };

  const updatePhotoTagToFinalTag = async (): Promise<boolean> => {
    try {
      const tag = getRejectPhotoTag(moldOrder);
      const finalTag = `${tag}-final`;
      const rejectPhotos = files.filter(file => file.tag === tag);

      if (rejectPhotos.length > 0) {
        const fileNames = rejectPhotos.map(file => file.fileName);
        await fileService.addTagToFiles(fileNames, { tag: finalTag });
      }

      await updateMoldOrderDetail({ 'rejectPhotoTag': finalTag, });
      return true;
    } catch (error) {
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!modalContent.action) return;

    try {
      setIsSubmitting(true);
      
      // Auto-save nextStepAction fields (from user input or defaults) if not already set
      const nextStepValues = getNextStepActionValues();
      if (Object.keys(nextStepValues).length > 0) {
        await updateMoldOrderDetail(nextStepValues);
      }
      
      await updateMoldOrderStateInstance(moldOrder, { notes: note });

      if (modalContent.action === 'reject') {
        const result = await updatePhotoTagToFinalTag();
        if (!result) {
          showError('ขออภัย ระบบพบข้อผิดพลาดเกี่ยวกับรูปที่อัปโหลด กรุณาลองใหม่อีกครั้ง');
          return;
        }
      }

      if (onStatusUpdate) {
        if (modalContent.action === 'accept') {
          await onStatusUpdate(modalContent.action, nextAssignee?.id);
        } else {
          await onStatusUpdate(modalContent.action, null);
        }
      }

      setModalContent({
        title: 'สำเร็จ',
        message: 'ดำเนินการเรียบร้อยแล้ว',
        type: 'success',
      });
    } catch (error) {
      console.error('Error updating mold order:', error);
      setModalContent({
        title: 'เกิดข้อผิดพลาด',
        message: 'ไม่สามารถดำเนินการได้ กรุณาลองใหม่อีกครั้ง',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = async ({ modalContentType }: { modalContentType: string }) => {
    if (modalContentType === 'reject') {
      const rejectFiles = getRejectFiles();
      if (rejectFiles.length > 0) {
        setErrorMessage('คุณยังมีรูปภาพที่แนบอยู่ กรุณาลบไฟล์ที่แนบก่อนดำเนินการต่อ');
        setErrorType('tag-file-warning');
        setErrorModalOpen(true);
        return;
      }
    }
    setIsModalOpen(false);
  };

  const showError = (message: string) => {
    setErrorMessage(message);
    setErrorType('generic-error');
    setErrorModalOpen(true);
  };

  function closeErrorModal() {
    setErrorModalOpen(false);
    setErrorType(null);
    setErrorMessage(null);
  }

  const handleDeleteAllDraftFilesByTag = async () => {
    try {
      setIsDeletingAllDraftFiles(true);
      const draftTag = getDraftRejectPhotoTag(moldOrder);
      const filesToDelete = files.filter(file => file.tag === draftTag);
      if (filesToDelete.length === 0) return closeErrorModal();

      for (const file of filesToDelete) {
        const result = await fileService.deleteFile(file.fileName);
        if (!result.isSuccess) {
          return showError(result.errorMessage || `เกิดข้อผิดพลาดในการลบไฟล์ ${file.fileName}`);
        }
      }

      await fetchFiles();
      closeErrorModal();
    } catch (err) {
      showError('เกิดข้อผิดพลาดในการดำเนินการ');
    } finally {
      setIsDeletingAllDraftFiles(false);
    }
  };

  // =============================
  // Action orchestration and confirm flows
  // =============================
  const handleActionClick = (
    action: ActionType,
    acceptMode: 'submit' | 'approve' | null = null,
    messageOverride?: { submit?: string; reject?: string; }
  ) => {
    const getMessage = () => {
      switch (action) {
        case 'accept':
          const verbOverride = messageOverride?.submit ?? 'ส่งใบงาน';
          return acceptMode === 'submit' ? `คุณต้องการ${verbOverride}ใช่หรือไม่?` : 'คุณต้องการอนุมัติใช่หรือไม่?';
        case 'rework':
          return 'คุณต้องการส่งใบงานกลับเพื่อแก้ไขใช่หรือไม่?';
        case 'reject':
          const rejectOverride = messageOverride?.reject ?? 'ปฏิเสธใบงาน';
          return `คุณต้องการ${rejectOverride}ใช่หรือไม่?`;
        default:
          return '';
      }
    }
    const message = getMessage();
    const modalType = action === 'reject' ? action : (
      action === 'accept' ? (acceptMode === 'submit' ? 'confirmation' : 'accept') : action
    );

    setModalContent({
      title: 'ยืนยันการดำเนินการ',
      message,
      type: modalType,
      action,
    });
    setIsModalOpen(true);
  };

  const handleSmallConfirmModalConfirm = () => {
    setIsSmallConfirmModalOpen(false);
  };

  const handleSmallConfirmModalClose = () => {
    setIsSmallConfirmModalOpen(false);
  };

  // =============================
  // Render helpers
  // =============================
  const renderActionButtons = () => {
    const firstLevelType = getMoldOrderMainType(moldOrder.type);
    const buttonConfig = getActionButtonMapping(firstLevelType, moldOrder.status);
    const {
      buttons = [],
      buttonType,
      customAcceptButtonLabel,
      customRejectButtonLabel,
    } = buttonConfig || {};
    const showApprovalButtonAsSubmit = buttonType === 'submit';
    const needUserFillForm = buttons.includes('FillQuestionnaire');
    const showViewQuestionnaireButton = buttons.includes('ViewQuestionnaire');
    const shouldShowViewQuestionnaireButton = showViewQuestionnaireButton || !needUserFillForm;
    const approvalButtonText = customAcceptButtonLabel || (showApprovalButtonAsSubmit ? 'ส่งใบงาน' : 'อนุมัติ')
    const rejectButtonText = customRejectButtonLabel || (showApprovalButtonAsSubmit ? 'ปิดใบงาน' : 'ปฏิเสธ');
    const showPrioritizeButton = buttons.includes('PrioritizeMoldOrder');
    const showPrintQRCodeButton = buttons.includes('PrintQRCode');

    return (
      <>
        {showPrioritizeButton && <button
          onClick={() => navigate(`/sort-mold-orders/${moldOrder.id}`)}
          className="shadow-sm hover:shadow-md text-base w-full py-3 px-4 rounded-lg flex items-center justify-center bg-white border-2 border-blue-500 text-blue-500 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
          data-cy="sort-order-button"
        >
          <CalendarArrowDown className="h-4 w-4 mr-2" />
          <span>จัดลำดับใบงาน</span>
        </button>}
        {shouldShowViewQuestionnaireButton && <ViewQuestionnaireButton moldOrder={moldOrder} />}
        {needUserFillForm && <FillQuestionnaireButton
          moldOrder={moldOrder}
          progress={progress}
          navigate={navigate}
        />}
        {showPrintQRCodeButton && <PrintQRCodeButton moldId={moldOrder?.mold?.id ? parseInt(moldOrder.mold.id) : undefined} />}
        <ApprovalButtons
          acceptLabel={approvalButtonText}
          acceptStyle={showApprovalButtonAsSubmit ? 'submit' : 'approve'}
          rejectLabel={rejectButtonText}
          moldOrder={moldOrder}
          handleActionClick={handleActionClick} />
      </>
    );
  };

  const RejectMoldOrderByCreatorButton = () => {
    const isCreator = user?.user_id === moldOrder.createdBy.id;
    if (!isCreator) return <></>;
    switch (moldOrder.status) {
      case MoldOrderStatus.PENDING_ACKNOWLEDGE:
        return (<ActionButton
          buttonText="ปิดใบงาน"
          buttonType="reject"
          testIdIdentifier="reject-button"
          onClick={() => handleActionClick('reject')} />);
      default:
        return (<></>);
    }
  };

  // =============================
  // Download handler
  // =============================
  const handleDownloadReport = async () => {
    setLoadingPdfReportText("กำลังดาวน์โหลดรายงาน...");
    const file = await downloadPdfReport(moldOrder.id);
    setLoadingPdfReportText("");
    if (file) {
      const url = window.URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mold_order_${moldOrder.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  // =============================
  // Final-step memoized UI
  // =============================
  if (isFinalStep(moldOrder.status)) {
    const stepClassName = moldOrder.status === MoldOrderStatus.DONE ? 'bg-green-500 border-green-500 text-white' : 'bg-red-500 border-red-500 text-white';
    const title = moldOrder.status === MoldOrderStatus.DONE ? 'ใบงานได้รับการอนุมัติ' : 'ใบงานไม่ได้รับการอนุมัติ';
    const reviewInfo = getDisplayReviewInfo(moldOrder);
    const description = () => {
      const prefix = moldOrder.status === MoldOrderStatus.DONE ? 'อนุมัติ' : (
        moldOrder.status === MoldOrderStatus.END_REJECTED ? 'ไม่อนุมัติ' : ''
      );

      if (reviewInfo.reviewedAt) {
        return `${prefix}: ${format(reviewInfo.reviewedAt, 'dd/MM/yyyy HH:mm')}`
      }
      return '';
    }
    const styleClassName = moldOrder.status === MoldOrderStatus.DONE ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';

    // Memoize the reject files ImagesUploader component to prevent unnecessary re-renders in the final step section
    const finalStepRejectFilesUploader = useMemo(() => {
      if (!hasRejectFile({ moldOrder, isDraftState: false })) return null;

      const rejectPhotoTag = getFinalRejectPhotoTag(moldOrder);
      const filteredRejectFiles = files.filter(file => file.tag === rejectPhotoTag);

      return (
        <ImagesUploader
          key={`final-step-reject-files-${rejectPhotoTag}-${filteredRejectFiles.length}`}
          title="Reject"
          displayTitle="รูปภาพประกอบเหตุผลในการปฏิเสธ"
          moldOrder={moldOrder}
          entityId={moldOrder.id}
          entityType={'mold_order'}
          tag={rejectPhotoTag}
          displayStyle="inline"
          hideTag={true}
          files={files}
        />
      );
    }, [
      moldOrder.id,
      moldOrder.status,
      moldOrder.orderDetail?.rejectPhotoTag,
      files.length,
      hasRejectFile
    ]);
    const ResultIcon = moldOrder.status === MoldOrderStatus.END_REJECTED ? X : CheckCircle;

    return (
      <div className="flex-auto w-full lg:w-1/3 items-center justify-start flex flex-col gap-3">
        <div className={`w-full items-center justify-center flex flex-col px-2 md:px-6 py-3 md:py-6 rounded-md ${styleClassName}`}>
          <div
            className={`w-12 h-12 rounded-full flex items-center justify-center ${stepClassName}`}
          >
            <ResultIcon className="w-6 h-6" />
          </div>
          <h3 className="text-xl md:text-2xl text-center font-semibold my-3">{title}</h3>
          <p className="text-center mt-2">{description()}</p>
          <div className="flex flex-col items-start justify-between gap-2 space-x-2 mt-4 w-full bg-gray-100 px-2 md:px-4 py-3 rounded-md">
            {moldOrder.status === MoldOrderStatus.END_REJECTED && (
              <div className='w-full bg-white-50 p-2'>
                <div className="">
                  <p className="text-base font-bold text-red-700">เหตุผลในการปฏิเสธ:</p>
                  <ExpandableText
                    text={reviewInfo.reason}
                    textClassName="text-base text-red-500"
                    maxLines={5}
                  />
                </div>
                {finalStepRejectFilesUploader}
              </div>
            )}
            <div className="flex items-center gap-4">
              <UserProfileImage currentAssignee={null} />
              <div>
                <p className="text-base font-bold text-gray-700">ผู้ประเมิน:</p>
                <p className="text-base text-gray-500">{assignee.fullname || assignee.username}</p>
              </div>
            </div>
          </div>
        </div>
        <ViewQuestionnaireButton moldOrder={moldOrder} />
        {featureToggles.enabledGeneratePdfButton && (
          <ActionButton buttonText="ดาวน์โหลดใบงาน PDF" buttonType="downloadReport" onClick={handleDownloadReport} loadingText={loadingPdfReportText} />
        )}
        {moldOrder.status === MoldOrderStatus.END_REJECTED && (
          <CreateNewMoldOrderButton moldOrder={moldOrder} />
        )}
      </div>
    );
  }

  // =============================
  // Component return (UI)
  // =============================
  if (formConfigLoading || filesLoading) {
    return <NextStepSkeleton />;
  }
  const NextAssigneeComponent = () => {
    if (moldOrder.nextAssignableUsers && moldOrder.nextAssignableUsers.length > 0) {
      return <NextAssigneeInfo
        assignee={nextAssignee}
        moldOrder={moldOrder}
        assigneeList={moldOrder.nextAssignableUsers}
        onChangeAssignee={(assignee: User) => {
          setNextAssignee(assignee)
        }}
        isCurrentAssignee={false}
      />
    }
    return <></>
  }
  const UpdateActionComponent = () => {
    const formConfigs = getNextStepActionConfig(moldOrder.type, moldOrder.status);

    // Initialize form values based on formConfigs
    const [formValues, setFormValues] = useState<Record<string, any>>(() => {
      const initialValues: Record<string, any> = {};
      formConfigs.forEach(config => {
        if (moldOrder.orderDetail.hasOwnProperty(config.key)) {
          initialValues[String(config.key)] = moldOrder.orderDetail[config.key as keyof typeof moldOrder.orderDetail];
        } else if (config.type === 'date' && config.defaultDateOffset !== undefined) {
          initialValues[String(config.key)] = getBangkokDateWithOffset(config.defaultDateOffset)
            .toISOString()
            .slice(0, 10);
        }
      });
      return initialValues;
    });

    const [isExpanded, setIsExpanded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Sync initial values to parent ref
    React.useEffect(() => {
      updateActionFormValuesRef.current = formValues;
    }, []); // Only on mount

    // Don't render if no form configs
    if (formConfigs.length === 0) {
      return null;
    }

    const handleFieldChange = (key: string, value: any) => {
      setFormValues(prev => {
        const newValues = {
          ...prev,
          [key]: value
        };
        // Sync to parent ref so handleSubmit can access it
        updateActionFormValuesRef.current = newValues;
        return newValues;
      });
    };

    const handleSave = async () => {
      try {
        setIsSaving(true);
        await updateMoldOrderDetail(formValues);
        setIsExpanded(false);
        // Optional: Show success feedback
      } catch (error) {
        console.error('Error updating fields:', error);
        showError('ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง');
      } finally {
        setIsSaving(false);
      }
    };

    // Generate preview text for collapsed header
    const getPreviewText = () => {
      if (formConfigs.length === 1) {
        const config = formConfigs[0];
        const value = formValues[String(config.key)];
        if (config.type === 'date' && value) {
          return format(new Date(value), 'dd/MM/yyyy');
        }
        return value || '-';
      }
      return `${formConfigs.length} รายการ`;
    };

    return (
      <div className="border-2 border-slate-200 rounded-lg mb-3 overflow-hidden transition-all">
        {/* Compact header - always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-3 bg-blue-50 hover:bg-blue-100 transition-colors flex items-center justify-between group"
          data-cy="update-action-toggle"
          type="button"
        >
          <div className="flex items-center gap-2">
            <CalendarArrowDown className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium text-gray-800">
              {formConfigs[0].label}:
            </span>
            <span className="text-sm font-semibold text-blue-600">
              {getPreviewText()}
            </span>
          </div>
          <div className={`text-xs text-blue-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
            ▼
          </div>
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 py-3 bg-white border-t border-slate-200">
            <div className="flex flex-col gap-3">
              {/* Render form fields dynamically */}
              {formConfigs.map(config => {
                const fieldKey = String(config.key);
                const isRequired = typeof config.required === 'function' 
                  ? config.required(formValues) 
                  : config.required;
                return (
                  <div key={fieldKey} className="grid gap-2">
                    <label
                      htmlFor={`field-${fieldKey}`}
                      className="text-sm font-medium text-gray-700"
                    >
                      {config.label}{isRequired ? <span className="text-sm text-red-800">*</span> : ''}
                    </label>
                    <FormField
                      formType={config.type}
                      label={config.label}
                      value={formValues[fieldKey] || ''}
                      onChange={(value) => handleFieldChange(fieldKey, value)}
                      placeholder={config.placeholder}
                      required={isRequired}
                      testIdIdentifier={fieldKey}
                      id={`field-${fieldKey}`}
                    />
                  </div>
                );
              })}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-cy="save-update-action-button"
                >
                  {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  disabled={isSaving}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-cy="cancel-update-action-button"
                >
                  ยกเลิก
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="flex-auto w-full xl:w-1/3">
      <div className="w-full">
        <div className="flex flex-col sm:flex-col justify-between items-start">
          {/* Assignee Information */}
          <NextAssigneeInfo
            assignee={assignee}
            assigneeList={moldOrder.currentAssignableUsers}
            onChangeAssignee={changeCurrentAssignee}
            moldOrder={moldOrder}
            isCurrentAssignee={true}
            approvalInfo={approvalInfo}
          >
            <div className="flex flex-col gap-2">
              <span className="text-gray-600 font-medium text-xs sm:text-sm">
                การดำเนินการที่ทำได้ของคุณ {user?.full_name}:
              </span>
              {hasPermission ? (
                <div className="flex flex-col w-full bg-white rounded-xl shadow-sm gap-2" data-cy="action-buttons" data-testid="action-buttons">
                  {renderActionButtons()}
                </div>
              ) : (
                <div className="flex flex-col gap-3 w-full">
                  <AssignToMeButton
                    user={user}
                    assigneeList={moldOrder.currentAssignableUsers}
                    onChangeAssignee={changeCurrentAssignee}
                  />
                  <div className="flex flex-col space-y-3" data-cy="action-buttons" data-testid="action-buttons">
                    <ViewQuestionnaireButton moldOrder={moldOrder} />
                    <RejectMoldOrderByCreatorButton />
                  </div>
                </div>
              )}
            </div>
          </NextAssigneeInfo>
        </div>
      </div>

      <CustomModal
        isOpen={isModalOpen}
        title={modalContent.title}
        message={modalContent.message}
        modalType={modalContent.type}
        onClose={() => handleModalClose({ modalContentType: modalContent.type })}
        onConfirm={() => {
          if (['confirmation', 'accept', 'reject', 'rework'].includes(modalContent.type)) {
            handleSubmit();
          } else {
            handleModalClose({ modalContentType: modalContent.type });
          }
        }}
        isLoading={isSubmitting}
        note={note}
        setNote={setNote}
        isAllowSetNextAssignee={showNextAssineeSelection}
        rejectReasons={moldOrder.status === MoldOrderStatus.REPORTER_CONFIRM && modalContent.type == 'reject' ? reporterRejectReasons : null}
        rejectPhotos={getRejectFiles()} // Use function to get reject files instead of maintaining state
        files={files}
        moldOrder={moldOrder}
        onFileChange={fetchFiles}
        NextAssigneeComponent={NextAssigneeComponent}
        UpdateActionComponent={UpdateActionComponent}
      >
      </CustomModal>

      <SmallConfirmModal
        isOpen={isSmallConfirmModalOpen}
        onClose={handleSmallConfirmModalClose}
        onConfirm={handleSmallConfirmModalConfirm}
        isConfirmButtonEnabled={allowFormSubmission}
        message={''}
        closeButtonLabel='ปิดเพื่อกรอกฟอร์ม'
        confirmButtonLabel='ยืนยันส่งใบงาน'
      />

      {errorType === 'tag-file-warning' && (
        <TagFileWarningModal
          isOpen={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          errorMessage={errorMessage}
          onDeleteAll={() => handleDeleteAllDraftFilesByTag()}
          tagLabel="ภาพทั้งหมด"
        />
      )}
      {errorType === 'generic-error' && (
        <GenericErrorModal
          isOpen={errorModalOpen}
          onClose={() => setErrorModalOpen(false)}
          errorMessage={errorMessage}
        />
      )}
      {isDeletingAllDraftFiles && (<DeletingModal />)}
    </div>
  );
};

export default NextStepActionBox;
